import React, { Component } from "react";
import { Button, Card } from "react-bootstrap";
import gql from "graphql-tag";
import * as Cookies from "es-cookie";
import CardLessDataTable from "../datatables/CardLessDataTableWithSearch";
import { normalizeAttachments } from "./expenseUtils";
import { ExpenseTableShimmer } from "./ExpenseShimmer";
import { attachModuleGuard, AccessDeniedModal, AccessDeniedCard } from "../../../utils/moduleGuard";

// /expense-management → "expense_management". Match on module_key only.
//
// NOTE: this tab already has its OWN, unrelated permission axis — `getExpenseUserPermission
// .isApprover` from the backend, which early-returns an "Access Restricted" card. That is a
// separate concept from the role module grant and is left exactly as it was; the guard below
// is the module-level is_edit check layered on top.
const EXPENSE_MODULE = ["expense_management"];

const GET_PENDING_EXPENSES = gql`
  query ($filter: ExpenseFilterInput!) {
    getExpenses(filter: $filter) {
      id
      expense_date
      amount
      tax_amount
      discount_amount
      final_amount
      payment_status
      description
      reference_no
      expense_head_id
      expense_category_id
      expense_vendor_id
      head_name
      category_name
      vendor_name
      attachments
      payment_mode_id
    }
  }
`;

const APPROVE_EXPENSE = gql`
  mutation ($id: ID!, $status: String!, $userId: Int!) {
    approveExpense(id: $id, status: $status, userId: $userId) {
      id
      payment_status
    }
  }
`;

const THEME = {
  primary: "#6366f1",
  success: "#10b981",
  danger: "#ef4444",
  warning: "#f59e0b",
  text: "#1f2937",
  textMuted: "#6b7280",
  bgLight: "#f9fafb",
  border: "#e5e7eb",
};

class PendingApprovals extends Component {
  constructor(props) {
    super(props);
    this.state = {
      expenses: [],
      loading: true,
      activeStatus: "pending", // 'pending', 'Paid', 'Rejected'
      accessDeniedShow: false
    };

    this.guard = attachModuleGuard(this, EXPENSE_MODULE);
  }

  componentDidMount() {
    // is_view === 0 → don't fetch the expenses list. render() returns Access Denied, so
    // bailing here is what keeps the approvals data off the wire.
    if (!this.guard.canView()) return;

    this.fetchExpenses();
  }

  fetchExpenses = async () => {
    const { client } = this.props;
    const { activeStatus } = this.state;
    const instId = parseInt(Cookies.get("institutionid") || Cookies.get("institute_id") || "0");

    this.setState({ loading: true });
    try {
      const { data } = await client.query({
        query: GET_PENDING_EXPENSES,
        variables: {
          filter: {
            institute_id: instId,
            status: activeStatus,
          },
        },
        fetchPolicy: "network-only",
      });

      this.setState({
        expenses: data.getExpenses || [],
        loading: false,
      });
    } catch (err) {
      console.error("Error fetching expenses:", err);
      this.setState({ loading: false });
    }
  };

  handleStatusChange = (status) => {
    this.setState({ activeStatus: status, expenses: [] }, () => {
      this.fetchExpenses();
    });
  };

  // Approve / Reject. Both write a status back to the expense → is_edit (Reject is a
  // status change, not a delete — the record survives). Checked before the confirm prompt.
  handleAction = async (id, status) => {
    if (!this.guard.requireEdit()) return;

    const { client } = this.props;
    const userId = parseInt(Cookies.get("id") || Cookies.get("user_id") || "0");
    const actionWord = status === "Paid" ? "approve" : "reject";
    if (!window.confirm(`Are you sure you want to ${actionWord} this expense record?`)) return;

    try {
      await client.mutate({
        mutation: APPROVE_EXPENSE,
        variables: { id, status, userId },
      });
      alert(`Expense ${status === "Paid" ? "approved" : "rejected"} successfully!`);
      this.fetchExpenses();
    } catch (err) {
      alert("Error processing action: " + err.message);
    }
  };

  formatDate = (val) => {
    if (!val) return "—";
    const num = Number(val);
    const d = isNaN(num) ? new Date(val) : new Date(num);
    return d instanceof Date && !isNaN(d) ? d.toLocaleDateString("en-GB", { day: '2-digit', month: 'short', year: 'numeric' }) : "—";
  };

  getColumns() {
    const { activeStatus } = this.state;
    const { configs } = this.props;
    const paymentModes = configs?.getPaymentModes || [];

    const cols = [
      {
        dataField: "id",
        text: "#",
        formatter: (cell, row, rowIndex) => rowIndex + 1,
        sort: true,
        headerStyle: { width: "70px", textAlign: "center" },
        style: { textAlign: "center" }
      },
      {
        dataField: "expense_date",
        text: "Date",
        sort: true,
        formatter: (cell) => this.formatDate(cell),
        headerStyle: { whiteSpace: "nowrap" }
      },
      {
        dataField: "reference_no",
        text: "Ref No",
        sort: true,
        formatter: (cell) => (
          <span style={{ background: "#f1f5f9", color: "#475569", borderRadius: 6, padding: "3px 8px", fontSize: 11, fontWeight: 600 }}>
            {cell || "—"}
          </span>
        ),
        headerStyle: { whiteSpace: "nowrap" }
      },
      {
        dataField: "head_name",
        text: "Expense Head",
        sort: true,
        formatter: (cell) => <strong style={{ color: "#1f2937" }}>{cell}</strong>,
        headerStyle: { whiteSpace: "nowrap" }
      },
      {
        dataField: "category_name",
        text: "Sub Category",
        sort: true,
        formatter: (cell) => cell || "—",
        headerStyle: { whiteSpace: "nowrap" }
      },
      {
        dataField: "vendor_name",
        text: "Vendor",
        sort: true,
        formatter: (cell) => cell || "Direct",
        headerStyle: { whiteSpace: "nowrap" }
      },
      {
        dataField: "amount",
        text: "Base Amt",
        sort: true,
        align: "right",
        headerAlign: "right",
        formatter: (cell) => `₹${(cell || 0).toLocaleString("en-IN")}`
      },
      {
        dataField: "final_amount",
        text: "Final Amt",
        sort: true,
        align: "right",
        headerAlign: "right",
        formatter: (cell) => <strong style={{ color: "#dc2626" }}>₹{(cell || 0).toLocaleString("en-IN")}</strong>
      },
      {
        dataField: "payment_mode_id",
        text: "Payment Mode",
        sort: true,
        formatter: (cell) => {
          const pMode = paymentModes.find(m => Number(m.id) === Number(cell))?.payment_mode || "—";
          return (
            <span style={{ background: "#eff6ff", color: "#2563eb", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600 }}>
              {pMode}
            </span>
          );
        }
      },
      {
        dataField: "attachments",
        text: "Doc",
        formatter: (cell) => {
          const attachmentsList = normalizeAttachments(cell);
          if (attachmentsList.length > 0) {
            return (
              <a href={attachmentsList[0].url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                <span style={{ background: "#fee2e2", color: "#ef4444", borderRadius: 6, padding: "3px 8px", fontSize: 11, fontWeight: 700 }} title={attachmentsList[0].name}>
                  <i className="fal fa-file-pdf mr-1" /> PDF
                </span>
              </a>
            );
          }
          return <span style={{ color: "#d1d5db", fontSize: 11 }}>—</span>;
        }
      },
      {
        dataField: "description",
        text: "Description",
        sort: true,
        formatter: (cell) => <span title={cell}>{cell || "—"}</span>,
        style: { maxWidth: 180, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }
      }
    ];

    if (activeStatus === "pending") {
      cols.push({
        dataField: "actions",
        text: "Actions",
        headerStyle: { width: "200px", textAlign: "center" },
        style: { textAlign: "center", whiteSpace: "nowrap" },
        formatter: (cell, row) => (
          <div>
            <Button
              variant="success"
              size="sm"
              onClick={() => this.handleAction(row.id, "Paid")}
              style={{ background: THEME.success, border: "none", padding: "4px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600, marginRight: 6 }}
            >
              <i className="fal fa-check mr-1" /> Approve
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => this.handleAction(row.id, "Rejected")}
              style={{ background: THEME.danger, border: "none", padding: "4px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600 }}
            >
              <i className="fal fa-times mr-1" /> Reject
            </Button>
          </div>
        )
      });
    }

    return cols;
  }

  render() {
    // is_view gate — self-contained, above the isApprover check because they are separate
    // axes: module_key is_view (RBAC) vs. the server-side approver flag. A role without
    // is_view is denied outright and never reads the approvals list.
    if (!this.guard.canView()) {
      return <AccessDeniedCard message="You do not have permission to view Approvals." />;
    }

    const { configs } = this.props;
    const { expenses, loading, activeStatus } = this.state;

    const isApprover = configs?.getExpenseUserPermission?.isApprover || false;

    if (!isApprover) {
      return (
        <div style={{ border: `1px solid ${THEME.border}`, borderRadius: 12, background: "#fff", padding: "40px 30px", textAlign: "center" }}>
          <div style={{ background: "#fef2f2", color: THEME.danger, width: 64, height: 64, borderRadius: "50%", display: "flex", alignItems: "center", margin: "0 auto 16px", justifyContent: "center" }}>
            <i className="fal fa-shield-alt" style={{ fontSize: 28 }} />
          </div>
          <h5 style={{ fontWeight: 700, color: THEME.text, marginBottom: 8 }}>Access Restricted</h5>
          <p style={{ color: THEME.textMuted, fontSize: 13, maxWidth: 450, margin: "0 auto 16px" }}>
            You do not have administrative permissions to approve or reject expenses. Please contact your system administrator if this is an error.
          </p>
        </div>
      );
    }

    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
          <h5 style={{ margin: 0, fontWeight: "800", color: "#1f2937", fontSize: "18px" }}>
            <i className="fal fa-clipboard-check mr-2" style={{ color: THEME.primary, background: "#eff6ff", padding: "8px", borderRadius: "8px" }} />
            Approvals Pipeline
          </h5>
          <span style={{ background: "#eff6ff", color: THEME.primary, borderRadius: 20, padding: "4px 14px", fontSize: 12, fontWeight: 700 }}>
            {expenses.length} Records
          </span>
        </div>

        {/* Status Sub-Tabs using standard Bootstrap nav nav-tabs */}
        <div style={{ borderBottom: `1px solid ${THEME.border}`, marginBottom: "24px", paddingBottom: "2px" }}>
          <ul className="nav nav-tabs" style={{ borderBottom: "none", marginBottom: "-2px", gap: "8px" }}>
            <li className="nav-item">
              <a 
                className={`nav-link ${activeStatus === "pending" ? "active" : ""}`}
                href="#"
                onClick={(e) => { e.preventDefault(); this.handleStatusChange("pending"); }}
                style={{ 
                  border: "none", 
                  borderBottom: activeStatus === "pending" ? `3px solid ${THEME.primary}` : "3px solid transparent", 
                  color: activeStatus === "pending" ? THEME.primary : THEME.textMuted, 
                  fontWeight: "700", 
                  fontSize: "14px",
                  padding: "10px 16px",
                  background: "transparent"
                }}
              >
                Pending Approvals
              </a>
            </li>
            <li className="nav-item">
              <a 
                className={`nav-link ${activeStatus === "Paid" ? "active" : ""}`}
                href="#"
                onClick={(e) => { e.preventDefault(); this.handleStatusChange("Paid"); }}
                style={{ 
                  border: "none", 
                  borderBottom: activeStatus === "Paid" ? `3px solid ${THEME.success}` : "3px solid transparent", 
                  color: activeStatus === "Paid" ? THEME.success : THEME.textMuted, 
                  fontWeight: "700", 
                  fontSize: "14px",
                  padding: "10px 16px",
                  background: "transparent"
                }}
              >
                Approved
              </a>
            </li>
            <li className="nav-item">
              <a 
                className={`nav-link ${activeStatus === "Rejected" ? "active" : ""}`}
                href="#"
                onClick={(e) => { e.preventDefault(); this.handleStatusChange("Rejected"); }}
                style={{ 
                  border: "none", 
                  borderBottom: activeStatus === "Rejected" ? `3px solid ${THEME.danger}` : "3px solid transparent", 
                  color: activeStatus === "Rejected" ? THEME.danger : THEME.textMuted, 
                  fontWeight: "700", 
                  fontSize: "14px",
                  padding: "10px 16px",
                  background: "transparent"
                }}
              >
                Rejected
              </a>
            </li>
          </ul>
        </div>

        <div>
          {loading ? (
            <ExpenseTableShimmer rows={6} cols={7} />
          ) : (
            <div style={{ paddingTop: "8px" }}>
              <CardLessDataTable
                parentData={expenses}
                particlecolumns={this.getColumns()}
                search={true}
                name={`approvals_${activeStatus}`}
                placeholder="Search Approvals..."
              />
            </div>
          )}
        </div>

        {/* Shown instead of approving / rejecting when is_edit is missing. This tab has
            no delete surface. */}
        <AccessDeniedModal
          show={this.state.accessDeniedShow}
          onHide={this.guard.hideAccessDenied}
        />
      </div>
    );
  }
}

export default PendingApprovals;
